import { API_BASE, apiPost, pruefeKontingentWarnung } from './api';
import { holeToken } from './sitzung';

export interface DiktatAufnahme {
  id: number;
  lehrer_id: number;
  transcript: string | null;
  audio_dauer_sek: number | null;
  status: 'aufgenommen' | 'transkribiert' | 'klassifiziert' | 'verarbeitet' | 'fehler';
  eingang: 'global_fab' | 'quick_mic';
  quick_mic_modul: string | null;
  fehler_text: string | null;
  created_at: string;
  updated_at: string;
}

export type DiktatModul = 'fehlzeit' | 'mehrarbeit' | 'notiz' | 'termin' | 'task';
/** Welle B3: das allgemeine Diktat erkennt zusätzlich Mitarbeit, Note und Gespräch (nicht als Quick-Mic) */
export type DiktatAktionModul = DiktatModul | 'mitarbeit' | 'note' | 'gespraech';

export interface DiktatAktion {
  id: number;
  aufnahme_id: number;
  modul: DiktatAktionModul;
  confidence_score: number | null;
  kritikalitaet: 'unkrit' | 'mittel' | 'hoch';
  daten_json: string;
  text_quelle: string | null;
  status: 'offen' | 'bestaetigt' | 'verworfen' | 'editiert';
  bestaetigte_daten_json: string | null;
  ziel_id: number | null;
  ziel_tabelle: string | null;
  created_at: string;
  entschieden_at: string | null;
}

// Welle P1b: schueler_id = Diktat aus einem Schülerprofil („Über … diktieren“) — die KI ordnet alles diesem Kind zu
export async function diktatStart(
  eingang: 'global_fab' | 'quick_mic',
  quick_mic_modul?: string,
  schueler_id?: number | null,
): Promise<{ id: number }> {
  return apiPost<{ id: number }>('/api/diktat/aufnahme/start', { eingang, quick_mic_modul, ...(schueler_id ? { schueler_id } : {}) });
}

/** Kind, über das im Profil diktiert wird */
export type DiktatKind = { id: number; name: string; vorname: string };

export async function diktatAudio(
  id: number,
  audioBlob: Blob,
  ext: string,
): Promise<{ transcript: string }> {
  const form = new FormData();
  form.append('audio', audioBlob, `aufnahme.${ext}`);
  const jwt = holeToken();
  const r = await fetch(`${API_BASE}/api/diktat/aufnahme/${id}/audio`, {
    method: 'POST',
    headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
    body: form,
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({} as any));
    throw new Error(e.error === 'kontingent' ? e.message : (e.fehler ?? `Fehler ${r.status}`));
  }
  const data = await r.json();
  pruefeKontingentWarnung(data); // Welle D2: 80%-Hinweis (gedrosselt)
  return data;
}

export async function diktatVerarbeiten(id: number): Promise<{ aktionen: DiktatAktion[] }> {
  // apiPost übernimmt Kontingent-Meldung (S3) + 80%-Hinweis (D2)
  return apiPost<{ aktionen: DiktatAktion[] }>(`/api/diktat/aufnahme/${id}/verarbeiten`);
}

export async function diktatAktionBestaetigen(
  aktionId: number,
  daten: Record<string, unknown>,
  // Welle B3: als='mitarbeit' („Als Mitarbeit werten“ einer Notiz), ersetzen = Wochenwert ersetzen
  optionen: { als?: 'mitarbeit'; ersetzen?: boolean } = {},
): Promise<{ ok: boolean; ziel_id: number | null; ziel_tabelle: string | null; hinweis?: string }> {
  return apiPost<{ ok: boolean; ziel_id: number | null; ziel_tabelle: string | null; hinweis?: string }>(
    `/api/diktat/aktion/${aktionId}/bestaetigen`, { daten, ...optionen });
}

export async function diktatAktionVerwerfen(aktionId: number): Promise<{ ok: boolean }> {
  return apiPost<{ ok: boolean }>(`/api/diktat/aktion/${aktionId}/verwerfen`);
}
